import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconUserPenFill18 as AssigneeIcon,
	IconOffice3Fill18 as CompanyIcon,
	IconArrowsRotateCenterFill18 as OriginIconCat,
	IconTagsFill18 as TagIcon,
} from "nucleo-ui-fill-18"
import {
	IconCheckOutline18 as Check,
	IconLink2SlashOutline18 as Link2Off,
} from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import type { FeedbackOrigin } from "@productlane/db/browser"
import type { IconType } from "@productlane/ui"
import type { TeamMember } from "@productlane/zero/queries"
import { FeedbackOriginTextRecord } from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"
import { Avatar } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { FilterChip, SearchableFilterItem } from "@/components/filters"
import {
	FilterControlChips,
	FilterPopover,
	useFilteredSearchItems,
} from "@/components/filters"
import { Tag } from "@/components/tag"
import { companyDomain } from "@/lib/companies"
import { FeedbackOriginIconRecord } from "@/lib/feedback"
import { useCompanyOptions } from "@/lib/use-entity-options"
import { useAuthData } from "@/lib/zero"
import { CompanyLogo } from "@/screens/app/customers/company-logo"

export interface ReportingFilters {
	origins: FeedbackOrigin[]
	assigneeIds: Array<string | null>
	tagIds: string[]
	companyIds: Array<string | null>
}

export const EMPTY_REPORTING_FILTERS: ReportingFilters = {
	origins: [],
	assigneeIds: [],
	tagIds: [],
	companyIds: [],
}

export const toReportingFilterInput = (filters: ReportingFilters) => ({
	origins: filters.origins.length > 0 ? filters.origins : undefined,
	assigneeIds: filters.assigneeIds.length > 0 ? filters.assigneeIds : undefined,
	tagIds: filters.tagIds.length > 0 ? filters.tagIds : undefined,
	companyIds: filters.companyIds.length > 0 ? filters.companyIds : undefined,
})

type Cat = "Assignee" | "Origin" | "Tag" | "Company"

const CATEGORY_ORDER: Cat[] = ["Assignee", "Origin", "Tag", "Company"]

const CAT_LABEL: Record<Cat, string> = {
	Assignee: "Assignee",
	Origin: "Origin",
	Tag: "Tag",
	Company: "Company",
}

const CAT_ICON: Record<Cat, IconType> = {
	Assignee: AssigneeIcon,
	Origin: OriginIconCat,
	Tag: TagIcon,
	Company: CompanyIcon,
}

interface OriginEntry {
	key: string
	label: string
	Icon: IconType
	origins: FeedbackOrigin[]
}

const GROUPED_ORIGINS: OriginEntry[] = [
	{
		key: "slack",
		label: "Slack",
		Icon: FeedbackOriginIconRecord.SLACK,
		origins: ["SLACK", "SLACK_CONNECT"],
	},
	{
		key: "intercom",
		label: "Intercom",
		Icon: FeedbackOriginIconRecord.INTERCOM,
		origins: ["INTERCOM", "INTERCOM_ATTACHMENT"],
	},
	{
		key: "zendesk",
		label: "Zendesk",
		Icon: FeedbackOriginIconRecord.ZENDESK,
		origins: ["ZENDESK", "ZENDESK_ATTACHMENT"],
	},
]

const INDIVIDUAL_ORIGINS: FeedbackOrigin[] = [
	"EMAIL",
	"PORTAL",
	"SUPPORT_PORTAL",
	"WIDGET",
	"AI_CHAT",
	"LIVE_CHAT",
	"INAPP",
	"LINEAR",
	"API",
	"CUSTOM",
	"TEAMS",
	"ZAPIER",
	"HUBSPOT",
	"CALENDAR",
]

const ORIGIN_ENTRIES: OriginEntry[] = [
	...GROUPED_ORIGINS,
	...INDIVIDUAL_ORIGINS.map((origin) => ({
		key: origin,
		label: FeedbackOriginTextRecord[origin].replace("via ", ""),
		Icon: FeedbackOriginIconRecord[origin],
		origins: [origin],
	})),
]

function toggle<T>(list: T[], value: T): T[] {
	return list.includes(value)
		? list.filter((v) => v !== value)
		: [...list, value]
}

function toggleOriginGroup(filters: ReportingFilters, entry: OriginEntry) {
	const selected = entry.origins.some((o) => filters.origins.includes(o))
	return {
		...filters,
		origins: selected
			? filters.origins.filter((o) => !entry.origins.includes(o))
			: [...filters.origins, ...entry.origins],
	}
}

function originGroupCount(filters: ReportingFilters) {
	const groups = new Set<string>()
	for (const o of filters.origins) {
		const group = ORIGIN_ENTRIES.find((g) => g.origins.includes(o))
		groups.add(group ? group.key : o)
	}
	return groups.size
}

function useTags() {
	const decodedToken = useAuthData()
	const [tagsRaw] = useQuery(queries.tags(decodedToken))
	return useMemo(() => {
		const seen = new Set<string>()
		return tagsRaw.filter((t) => {
			if (seen.has(t.id)) return false
			seen.add(t.id)
			return true
		})
	}, [tagsRaw])
}

interface CategoryPanelProps {
	cat: Cat
	filters: ReportingFilters
	onChange: (next: ReportingFilters) => void
	teamMembers: readonly TeamMember[]
	tags: ReturnType<typeof useTags>
	onAfterSelect?: () => void
}

function CategoryPanel({
	cat,
	filters,
	onChange,
	teamMembers,
	tags,
	onAfterSelect,
}: CategoryPanelProps) {
	const [query, setQuery] = useState("")

	const checkClass = (selected: boolean) =>
		cn("h-4 w-4 shrink-0", { "opacity-100": selected, "opacity-0": !selected })

	const { companies: filteredCompanies } = useCompanyOptions({
		query,
		selectedIds: filters.companyIds,
		enabled: cat === "Company",
	})

	return (
		<Command shouldFilter={cat !== "Company"}>
			<CommandInput
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[min(70dvh,320px)]">
				<CommandEmpty>No results found.</CommandEmpty>
				<CommandGroup>
					{cat === "Assignee" && (
						<>
							<CommandItem
								value="Unassigned"
								onSelect={() => {
									onChange({
										...filters,
										assigneeIds: toggle(filters.assigneeIds, null),
									})
									onAfterSelect?.()
								}}
							>
								<div className="flex w-full items-center gap-2">
									<Avatar name="No assignee" showAvatarFallback size="xs" />
									<span className="flex-1 truncate">Unassigned</span>
									<Check
										className={checkClass(filters.assigneeIds.includes(null))}
									/>
								</div>
							</CommandItem>
							{teamMembers.map((member) => {
								const label = member.user?.name || member.user?.email || ""
								const selected = filters.assigneeIds.includes(member.userId)
								return (
									<CommandItem
										key={member.id}
										value={label}
										onSelect={() => {
											onChange({
												...filters,
												assigneeIds: toggle(filters.assigneeIds, member.userId),
											})
											onAfterSelect?.()
										}}
									>
										<div className="flex w-full items-center gap-2">
											<Avatar
												image={member.user?.imageUrl}
												name={label}
												size="xs"
											/>
											<span className="flex-1 truncate">{label}</span>
											<Check className={checkClass(selected)} />
										</div>
									</CommandItem>
								)
							})}
						</>
					)}

					{cat === "Origin" &&
						ORIGIN_ENTRIES.map((entry) => {
							const selected = entry.origins.some((o) =>
								filters.origins.includes(o),
							)
							return (
								<CommandItem
									key={entry.key}
									value={entry.label}
									onSelect={() => {
										onChange(toggleOriginGroup(filters, entry))
										onAfterSelect?.()
									}}
								>
									<div className="flex w-full items-center gap-2">
										<entry.Icon className="h-4 w-4 shrink-0 text-secondary" />
										<span className="flex-1 truncate">{entry.label}</span>
										<Check className={checkClass(selected)} />
									</div>
								</CommandItem>
							)
						})}

					{cat === "Tag" &&
						tags.map((tag) => {
							const selected = filters.tagIds.includes(tag.id)
							return (
								<CommandItem
									key={tag.id}
									value={tag.name}
									onSelect={() => {
										onChange({
											...filters,
											tagIds: toggle(filters.tagIds, tag.id),
										})
										onAfterSelect?.()
									}}
								>
									<div className="flex w-full items-center gap-2">
										<Tag tag={tag} />
										<span className="flex-1" />
										<Check className={checkClass(selected)} />
									</div>
								</CommandItem>
							)
						})}

					{cat === "Company" && (
						<>
							<CommandItem
								value="No company linked"
								onSelect={() => {
									onChange({
										...filters,
										companyIds: toggle(filters.companyIds, null),
									})
									onAfterSelect?.()
								}}
							>
								<div className="flex w-full items-center gap-2">
									<Link2Off className="h-4 w-4 shrink-0" />
									<span className="flex-1 truncate">No company linked</span>
									<Check
										className={checkClass(filters.companyIds.includes(null))}
									/>
								</div>
							</CommandItem>
							{filteredCompanies.length > 0 ? (
								<div className="h-64">
									<FixedSizeList
										height={256}
										width="100%"
										itemCount={filteredCompanies.length}
										itemData={filteredCompanies}
										itemKey={(index, data) => data[index]?.id ?? index}
										itemSize={36}
										overscanCount={5}
										className="!h-full"
									>
										{({ data, index, style }) => {
											const company = data[index]
											if (!company) return null
											const selected = filters.companyIds.includes(company.id)
											return (
												<CommandItem
													value={company.name}
													onSelect={() => {
														onChange({
															...filters,
															companyIds: toggle(
																filters.companyIds,
																company.id,
															),
														})
														onAfterSelect?.()
													}}
													style={style}
												>
													<div className="flex w-full items-center gap-2">
														<CompanyLogo
															linearLogoUrl={company.logoUrl ?? undefined}
															brandfetchData={null}
															fallback={company.name}
															size="xs"
														/>
														<span className="flex min-w-0 flex-1 items-center gap-1.5">
															<span className="truncate text-primary">
																{company.name}
															</span>
															{companyDomain(company) ? (
																<span className="truncate text-secondary">
																	{companyDomain(company)}
																</span>
															) : null}
														</span>
														<Check className={checkClass(selected)} />
													</div>
												</CommandItem>
											)
										}}
									</FixedSizeList>
								</div>
							) : null}
						</>
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

export function ReportingFilterButton({
	filters,
	onFiltersChange,
	teamMembers,
}: {
	filters: ReportingFilters
	onFiltersChange: (filters: ReportingFilters) => void
	teamMembers: readonly TeamMember[]
}) {
	const tags = useTags()
	const [globalQuery, setGlobalQuery] = useState("")
	const { companies } = useCompanyOptions({
		query: globalQuery,
		selectedIds: filters.companyIds,
	})

	const categories = useMemo(
		() =>
			CATEGORY_ORDER.map((type) => ({
				type,
				label: CAT_LABEL[type],
				Icon: CAT_ICON[type],
			})),
		[],
	)

	const counts = useMemo<Record<Cat, number>>(
		() => ({
			Assignee: filters.assigneeIds.length,
			Origin: originGroupCount(filters),
			Tag: filters.tagIds.length,
			Company: filters.companyIds.length,
		}),
		[filters],
	)

	const searchableItems = useMemo<SearchableFilterItem<Cat>[]>(() => {
		const items: SearchableFilterItem<Cat>[] = []

		items.push({
			id: "Assignee-none",
			category: "Assignee",
			categoryLabel: "Assignee",
			label: "Unassigned",
			searchText: "Unassigned",
			isSelected: filters.assigneeIds.includes(null),
			onSelect: () =>
				onFiltersChange({
					...filters,
					assigneeIds: toggle(filters.assigneeIds, null),
				}),
		})
		for (const member of teamMembers) {
			const label = member.user?.name || member.user?.email || ""
			items.push({
				id: `Assignee-${member.id}`,
				category: "Assignee",
				categoryLabel: "Assignee",
				label,
				searchText: label,
				renderCustom: () => (
					<Avatar image={member.user?.imageUrl} name={label} size="xs" />
				),
				isSelected: filters.assigneeIds.includes(member.userId),
				onSelect: () =>
					onFiltersChange({
						...filters,
						assigneeIds: toggle(filters.assigneeIds, member.userId),
					}),
			})
		}

		for (const entry of ORIGIN_ENTRIES) {
			items.push({
				id: `Origin-${entry.key}`,
				category: "Origin",
				categoryLabel: "Origin",
				label: entry.label,
				searchText: entry.label,
				Icon: entry.Icon,
				isSelected: entry.origins.some((o) => filters.origins.includes(o)),
				onSelect: () => onFiltersChange(toggleOriginGroup(filters, entry)),
			})
		}

		for (const tag of tags) {
			items.push({
				id: `Tag-${tag.id}`,
				category: "Tag",
				categoryLabel: "Tag",
				label: tag.name,
				searchText: tag.name,
				iconColor: tag.color,
				isSelected: filters.tagIds.includes(tag.id),
				onSelect: () =>
					onFiltersChange({
						...filters,
						tagIds: toggle(filters.tagIds, tag.id),
					}),
			})
		}

		items.push({
			id: "Company-none",
			category: "Company",
			categoryLabel: "Company",
			label: "No company linked",
			searchText: "No company linked",
			isSelected: filters.companyIds.includes(null),
			onSelect: () =>
				onFiltersChange({
					...filters,
					companyIds: toggle(filters.companyIds, null),
				}),
		})
		for (const company of companies) {
			items.push({
				id: `Company-${company.id}`,
				category: "Company",
				categoryLabel: "Company",
				label: company.name,
				secondaryLabel: companyDomain(company) ?? undefined,
				searchText: `${company.name} ${companyDomain(company) ?? ""}`,
				isSelected: filters.companyIds.includes(company.id),
				onSelect: () =>
					onFiltersChange({
						...filters,
						companyIds: toggle(filters.companyIds, company.id),
					}),
			})
		}

		return items
	}, [filters, teamMembers, tags, companies, onFiltersChange])

	const filteredSearchItems = useFilteredSearchItems(
		globalQuery,
		searchableItems,
	)

	return (
		<FilterPopover<Cat>
			categories={categories}
			counts={counts}
			renderSubPanel={(group, _q, _setQ, onFilterItemSelect) => (
				<CategoryPanel
					cat={group}
					filters={filters}
					onChange={onFiltersChange}
					teamMembers={teamMembers}
					tags={tags}
					onAfterSelect={onFilterItemSelect}
				/>
			)}
			searchableItems={searchableItems}
			filteredSearchItems={filteredSearchItems}
			onGlobalQueryChange={setGlobalQuery}
			tooltipLabel="Filter reporting"
		/>
	)
}

export function ReportingFilterChips({
	filters,
	onChange,
	teamMembers,
}: {
	filters: ReportingFilters
	onChange: (next: ReportingFilters) => void
	teamMembers: readonly TeamMember[]
}) {
	const tags = useTags()
	const firstCompanyId = filters.companyIds[0] ?? null
	const [firstCompany] = useQuery(queries.companyById(firstCompanyId ?? ""), {
		enabled: !!firstCompanyId,
	})

	const chips = useMemo<FilterChip[]>(() => {
		const result: FilterChip[] = []

		const panel = (cat: Cat) => (
			<CategoryPanel
				cat={cat}
				filters={filters}
				onChange={onChange}
				teamMembers={teamMembers}
				tags={tags}
			/>
		)

		if (filters.assigneeIds.length > 0) {
			const first = filters.assigneeIds[0]
			const firstLabel =
				first === null
					? "Unassigned"
					: (teamMembers.find((m) => m.userId === first)?.user?.name ??
						teamMembers.find((m) => m.userId === first)?.user?.email ??
						"")
			result.push({
				key: "Assignee",
				label: "Assignee",
				middleText: filters.assigneeIds.length > 1 ? "are" : "is",
				valueText:
					filters.assigneeIds.length === 1
						? firstLabel
						: `${filters.assigneeIds.length} selected`,
				onClear: () => onChange({ ...filters, assigneeIds: [] }),
				popoverContent: panel("Assignee"),
			})
		}

		const originGroups = originGroupCount(filters)
		if (originGroups > 0) {
			const firstEntry = ORIGIN_ENTRIES.find((e) =>
				e.origins.some((o) => filters.origins.includes(o)),
			)
			result.push({
				key: "Origin",
				label: "Origin",
				middleText: originGroups > 1 ? "are" : "is",
				valueText:
					originGroups === 1
						? (firstEntry?.label ?? "")
						: `${originGroups} selected`,
				onClear: () => onChange({ ...filters, origins: [] }),
				popoverContent: panel("Origin"),
			})
		}

		if (filters.tagIds.length > 0) {
			result.push({
				key: "Tag",
				label: "Tag",
				middleText: filters.tagIds.length > 1 ? "are" : "is",
				valueText:
					filters.tagIds.length === 1
						? (tags.find((t) => t.id === filters.tagIds[0])?.name ?? "")
						: `${filters.tagIds.length} selected`,
				onClear: () => onChange({ ...filters, tagIds: [] }),
				popoverContent: panel("Tag"),
			})
		}

		if (filters.companyIds.length > 0) {
			const first = filters.companyIds[0]
			const firstLabel =
				first === null ? "No company linked" : (firstCompany?.name ?? "")
			result.push({
				key: "Company",
				label: "Company",
				middleText: filters.companyIds.length > 1 ? "are" : "is",
				valueText:
					filters.companyIds.length === 1
						? firstLabel
						: `${filters.companyIds.length} selected`,
				onClear: () => onChange({ ...filters, companyIds: [] }),
				popoverContent: panel("Company"),
			})
		}

		return result
	}, [filters, teamMembers, tags, firstCompany, onChange])

	if (chips.length === 0) return null

	return (
		<FilterControlChips
			chips={chips}
			containerClassName="flex flex-wrap items-center gap-2"
		/>
	)
}
